import type {
	Asset,
	OverarchingAssetType,
} from '#modules/AssetOverview/AssetOverview.types'
import type { AssetCategory } from '#modules/Budget/types/expense.types'
import type { FC } from 'react'

import { FlowPage } from '@monthio/design-system'
import { LoadingScreen, notificationService } from '@monthio/react-components'
import { budgetApiClient } from '#api/budgetApiClient'
import { Formik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type {
	AssetBudgetDto,
	AssetBudgetFormValues,
	AssetBudgetPostDto,
} from '../AssetExpenses.types'

import { usePageNavigation } from '../usePageNavigation'
import { AssetBudget } from './AssetBudget'

interface MonthlyAssetExpensesPageProps {
	assets: Asset[]
	onCompleted: () => void
	assetCategory: AssetCategory
}

export const MonthlyAssetExpensesPage: FC<MonthlyAssetExpensesPageProps> = ({
	assets,
	onCompleted,
	assetCategory,
}) => {
	const { t } = useTranslation()

	const [assetBudgets, setAssetBudgets] = useState<AssetBudgetDto[]>()
	const [isFetchingData, setIsFetchingData] = useState(false)
	const [hasFetchingDataFailed, setHasFetchingDataFailed] = useState(false)

	useEffect(() => {
		const fetchBudgets = async () => {
			setIsFetchingData(true)
			const budgetsData = await budgetApiClient.getAssetBudgets(assetCategory)
			setAssetBudgets(budgetsData.assetBudgets)
			if (budgetsData.assetBudgets.length === 0) {
				onCompleted()
			}
		}
		if (!assetBudgets) {
			fetchBudgets()
				.catch(() => {
					setHasFetchingDataFailed(true)
				})
				.finally(() => setIsFetchingData(false))
		}
	}, [assetBudgets, assetCategory, onCompleted, t])

	const submitBudgets = async (_assetBudgets: AssetBudgetFormValues[]) => {
		try {
			const budgetsToPost: AssetBudgetPostDto[] = _assetBudgets.map(
				(budget) => ({
					// TODO: Verify that the type is correct or the type is lying and should be fixed
					assetId: budget.assetId ?? null,
					expenseEstimateBuckets: budget.expenseEstimateBuckets.map(
						({ bucket, monthlyAmount, expenseEstimates }) => ({
							bucket,
							monthlyAmount,
							items:
								expenseEstimates.length > 0
									? expenseEstimates.map((estimate) => ({
											description: estimate.name ?? estimate.type,
											addedByApplicant: estimate.addedByUser ?? false,
											monthlyAmount: estimate.monthlyAmount,
										}))
									: [],
						}),
					),
				}),
			)
			await budgetApiClient.updateAssetBudgets(budgetsToPost, assetCategory)
			onCompleted()
		} catch {
			notificationService.error(t('assetExpensesPage-submitBudgets-error'))
		}
	}

	if (hasFetchingDataFailed) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<FlowPage.Header title={t('assetExpensesPage-failure-text')} />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	if (isFetchingData) {
		return (
			<FlowPage>
				<FlowPage.Content $isCentered>
					<LoadingScreen />
				</FlowPage.Content>
			</FlowPage>
		)
	}

	if (assetBudgets == null || assetBudgets.length === 0) {
		return null
	}

	return (
		<Formik
			initialValues={{ budgets: assetBudgets }}
			onSubmit={(values) => submitBudgets(values.budgets)}
		>
			{({ values, submitForm }) => {
				return (
					<MonthlyAssetExpensesForm
						assetBudgets={values.budgets}
						assets={assets}
						submitForm={submitForm}
						assetCategory={assetCategory}
					/>
				)
			}}
		</Formik>
	)
}

const MonthlyAssetExpensesForm = ({
	assetBudgets,
	assets,
	submitForm,
	assetCategory,
}: {
	assetBudgets: AssetBudgetDto[]
	assets: Asset[]
	submitForm: () => void
	assetCategory: AssetCategory
}) => {
	const { t } = useTranslation()
	const { currentPage, goToNextPage, goBack } = usePageNavigation(
		assetBudgets.length,
	)
	const assetType: OverarchingAssetType =
		assetCategory === 'housing' ? 'realEstate' : 'vehicle'

	const onSubmit = () =>
		currentPage === assetBudgets.length - 1 ? submitForm() : goToNextPage()

	const assetPages = useMemo(
		() =>
			assetBudgets.map((budget, index) => (
				<AssetBudget
					key={budget.assetId}
					asset={assets.find((x) => x.id === budget.assetId)}
					assetBudget={budget}
					formInputId={`budgets[${index}]`}
					handleNotFoundAsset={goToNextPage}
				/>
			)),
		[assetBudgets, assets, goToNextPage],
	)

	return (
		<FlowPage>
			<FlowPage.Content $spacing="m">
				<FlowPage.Header
					title={t(
						`assetExpenses-MonthlyAssetExpensesPage-${assetType}-title`,
						{
							currentNumber: currentPage + 1,
							totalNumber: assetBudgets.length,
						},
					)}
					description={t(
						`assetExpenses-MonthlyAssetExpensesPage-${assetType}-subtitle`,
					)}
				/>
				{assetPages[currentPage]}
			</FlowPage.Content>
			<FlowPage.Footer
				actions={
					<FlowPage.Cta onClick={onSubmit}>
						{t('assetExpensesPage-continue-button')}
					</FlowPage.Cta>
				}
				backButton={
					currentPage > 0 ? (
						<FlowPage.BackButton onClick={goBack} ariaLabel={t('btn-back')} />
					) : undefined
				}
			/>
		</FlowPage>
	)
}
